import { useCallback, useEffect, useRef, useState } from 'react';

import observeResizeEvent from './observeResizeEvent';

import type { RefObject } from 'react';

interface UseTextInputValueOverflowProps {
  disabledOverflow?: boolean;
}

function isValidHtmlElement<T>(target: RefObject<T | null>): target is RefObject<T> {
  return target && target instanceof HTMLElement;
}

const useTextInputValueOverflow = <T extends HTMLElement = HTMLElement>(
  props: UseTextInputValueOverflowProps,
) => {
  const { disabledOverflow = false } = props;
  const containerRef = useRef<T>(null);

  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const handleShowTooltip = useCallback(() => {
    if (isValidHtmlElement<T>(containerRef)) {
      setShowTooltip(containerRef.current.offsetWidth < containerRef.current.scrollWidth);
    }
  }, [containerRef]);

  useEffect(() => {
    if (!disabledOverflow && isValidHtmlElement<T>(containerRef)) {
      const resizeCallback: ResizeObserverCallback = () => handleShowTooltip();
      return observeResizeEvent(containerRef, resizeCallback);
    }
  }, [containerRef, disabledOverflow, handleShowTooltip]);

  return { handleShowTooltip, showTooltip, containerRef };
};

export default useTextInputValueOverflow;
